/// <reference types="vite/client" />
/**
 * useAgentSocket — single WebSocket connection to SwarmFeedDO (#281).
 *
 * Exposes :
 *   - state: connecting | connected | reconnecting | disconnected
 *   - lastEventAt: ms timestamp of the most recent event (any type)
 *   - subscribe(type, handler) → unsubscribe()  — listen to events
 *   - sendCommand(type, payload) → Promise<result> — async command with ack
 *
 * Reconnect : exponential backoff 1s → 2s → 4s → … capped at 30s.
 * Ping every 30s ; reconnect if pong doesn't come in 10s.
 *
 * Token : reads sessionStorage[SK_TOKEN] = "levier.admin.token" (same
 * key as the /admin page from Phase 11.5 U3). When the token is empty
 * the hook stays in "disconnected" and never opens a socket — operator
 * must visit /admin first to set it.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  SwarmAck,
  SwarmEvent,
  type SwarmCommand,
} from "../../realtime/protocol";

const SK_TOKEN = "levier.admin.token";
const PING_INTERVAL_MS = 30_000;
const COMMAND_TIMEOUT_MS = 30_000;
const RECONNECT_CAP_MS = 30_000;

export type AgentSocketState =
  | "connecting"
  | "connected"
  | "reconnecting"
  | "disconnected";

export type EventHandler<T extends SwarmEvent["type"]> = (
  event: Extract<SwarmEvent, { type: T }>,
) => void;

export interface UseAgentSocketReturn {
  state: AgentSocketState;
  lastEventAt: number | null;
  subscribe: <T extends SwarmEvent["type"]>(type: T, handler: EventHandler<T>) => () => void;
  sendCommand: <R = unknown>(type: string, payload?: Record<string, unknown>) => Promise<R>;
}

interface PendingAck {
  resolve: (value: unknown) => void;
  reject: (err: Error) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

function readToken(): string {
  if (typeof window === "undefined") return "";
  return window.sessionStorage.getItem(SK_TOKEN) ?? "";
}

export function useAgentSocket(): UseAgentSocketReturn {
  const [state, setState] = useState<AgentSocketState>("disconnected");
  const [lastEventAt, setLastEventAt] = useState<number | null>(null);

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pingTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pendingAcks = useRef(new Map<string, PendingAck>());
  const handlers = useRef(new Map<string, Set<(e: SwarmEvent) => void>>());
  const wantOpenRef = useRef(true);

  const cleanup = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    if (pingTimer.current) clearInterval(pingTimer.current);
    reconnectTimer.current = null;
    pingTimer.current = null;
    for (const p of pendingAcks.current.values()) {
      clearTimeout(p.timeoutId);
      p.reject(new Error("WebSocket closed"));
    }
    pendingAcks.current.clear();
  }, []);

  const connect = useCallback(() => {
    const token = readToken();
    if (!token) {
      setState("disconnected");
      return;
    }

    setState((s) => (s === "connected" ? s : "connecting"));

    const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${proto}//${window.location.host}/api/ws?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      reconnectAttempts.current = 0;
      setState("connected");

      // Start the ping heartbeat.
      if (pingTimer.current) clearInterval(pingTimer.current);
      pingTimer.current = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send(JSON.stringify({ type: "ping" }));
        }
      }, PING_INTERVAL_MS);
    };

    ws.onmessage = (e) => {
      let raw: unknown;
      try {
        raw = JSON.parse(e.data as string);
      } catch {
        // eslint-disable-next-line no-console
        console.warn("[useAgentSocket] non-JSON message", e.data);
        return;
      }
      if (typeof raw !== "object" || raw === null || !("type" in raw)) return;
      const msg = raw as { type: string };

      // Heartbeat reply — nothing to do.
      if (msg.type === "pong") return;

      // Command ack — resolve / reject the pending promise.
      if (msg.type === "ack") {
        const ack = SwarmAck.safeParse(raw);
        if (!ack.success) return;
        const pending = pendingAcks.current.get(ack.data.id);
        if (!pending) return;
        if (ack.data.status === "executing") {
          // Keep the promise pending — done/error will resolve it.
          return;
        }
        clearTimeout(pending.timeoutId);
        pendingAcks.current.delete(ack.data.id);
        if (ack.data.status === "done") pending.resolve(ack.data.result);
        else pending.reject(new Error(ack.data.error));
        return;
      }

      // Otherwise it must be a SwarmEvent.
      const parsed = SwarmEvent.safeParse(raw);
      if (!parsed.success) {
        // eslint-disable-next-line no-console
        console.warn("[useAgentSocket] unknown event", msg.type);
        return;
      }
      setLastEventAt(Date.now());
      const set = handlers.current.get(parsed.data.type);
      set?.forEach((h) => h(parsed.data));
    };

    ws.onclose = () => {
      wsRef.current = null;
      if (pingTimer.current) clearInterval(pingTimer.current);
      pingTimer.current = null;
      if (!wantOpenRef.current) {
        setState("disconnected");
        return;
      }
      setState("reconnecting");
      const delay = Math.min(
        1000 * 2 ** reconnectAttempts.current,
        RECONNECT_CAP_MS,
      );
      reconnectAttempts.current += 1;
      reconnectTimer.current = setTimeout(connect, delay);
    };

    ws.onerror = () => {
      // onclose follows automatically — nothing to do.
    };
  }, []);

  useEffect(() => {
    wantOpenRef.current = true;
    connect();

    // React to storage changes (operator pastes the KB_SEED_TOKEN in
    // /admin from another tab — re-connect when it lands).
    const onStorage = (e: StorageEvent) => {
      if (e.key === SK_TOKEN) {
        if (wsRef.current) {
          wsRef.current.close();
        } else {
          connect();
        }
      }
    };
    window.addEventListener("storage", onStorage);

    return () => {
      wantOpenRef.current = false;
      window.removeEventListener("storage", onStorage);
      cleanup();
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [connect, cleanup]);

  const subscribe = useCallback(
    <T extends SwarmEvent["type"]>(type: T, handler: EventHandler<T>) => {
      const set = handlers.current.get(type) ?? new Set();
      set.add(handler as (e: SwarmEvent) => void);
      handlers.current.set(type, set);
      return () => {
        set.delete(handler as (e: SwarmEvent) => void);
      };
    },
    [],
  );

  const sendCommand = useCallback(
    <R = unknown>(
      type: string,
      payload?: Record<string, unknown>,
    ): Promise<R> => {
      return new Promise<R>((resolve, reject) => {
        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
          reject(new Error("WebSocket not connected"));
          return;
        }
        const id =
          typeof crypto !== "undefined" && "randomUUID" in crypto
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const timeoutId = setTimeout(() => {
          if (pendingAcks.current.has(id)) {
            pendingAcks.current.delete(id);
            reject(new Error(`command ${type} timed out`));
          }
        }, COMMAND_TIMEOUT_MS);
        pendingAcks.current.set(id, {
          resolve: resolve as (v: unknown) => void,
          reject,
          timeoutId,
        });
        const cmd: SwarmCommand = { id, type, payload };
        ws.send(JSON.stringify(cmd));
      });
    },
    [],
  );

  return { state, lastEventAt, subscribe, sendCommand };
}
